import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProjectNavigation from "../components/ProjectNavigation";
import ResponsiveImage from "../components/ResponsiveImage";

const FootwearDesign = () => {
  // Final images - organized by row
  // Toe-On view (own row)
  const finalFrontImage = { path: "/images/projects/Footwear Design/Final/front", label: "Toe-On View" };

  // Lateral images (three-quarter and profile)
  const finalLateralImages = [
    { path: "/images/projects/Footwear Design/Final/45lateral", label: "Three-Quarter Lateral" },
    { path: "/images/projects/Footwear Design/Final/flatlateral", label: "Lateral Profile" }
  ];

  // Medial images (three-quarter and profile)
  const finalMedialImages = [
    { path: "/images/projects/Footwear Design/Final/45medial", label: "Three-Quarter Medial" },
    { path: "/images/projects/Footwear Design/Final/flatmedial", label: "Medial Profile" }
  ];

  // Lifestyle shots
  const finalLifestyleImages = [
    { path: "/images/projects/Footwear Design/Final/on display", label: "Final Shoe on Display" },
    { path: "/images/projects/Footwear Design/Final/on foot", label: "Final Prototype on Foot" }
  ];

  // Process images - portrait (full upper pattern, leather upper nailed to last)
  const processPortraitImages = [
    { path: "/images/projects/Footwear Design/Process/both sides", label: "Full Upper Pattern", ext: "jpg" },
    { path: "/images/projects/Footwear Design/Process/on-last", label: "Leather Upper Nailed to Last, Set in Cup Sole", ext: "jpeg" }
  ];

  // Process images - landscape (original drawn upper pattern, upper pattern slashed and spread, felt prototype)
  const processLandscapeImages = [
    { path: "/images/projects/Footwear Design/Process/one side", label: "Original Drawn Upper Pattern", ext: "jpg" },
    { path: "/images/projects/Footwear Design/Process/slash and spread", label: "Upper Pattern, Slashed and Spread", ext: "jpg" },
    { path: "/images/projects/Footwear Design/Process/felt prototype", label: "Felt Prototype", ext: "jpg" }
  ];

  // Concept images
  const aiRenderImage = "/images/projects/Footwear Design/concept";
  const technicalSketchImage = "/images/projects/Footwear Design/Flat";
  const ideationSketchesImage = "/images/projects/Footwear Design/both art";

  // Alternative colorway images
  const alternativeColorways = [
    { path: "/images/projects/Footwear Design/Alternative CW/all white cw", label: "White Fullgrain Leather and Sole Colorway", ext: "png" },
    { path: "/images/projects/Footwear Design/Alternative CW/suede black cw", label: "Untrimmed Suede Black Colorway", ext: "png" },
    { path: "/images/projects/Footwear Design/Alternative CW/suede brown cw", label: "Suede Brown Colorway", ext: "png" }
  ];

  // Function to create an open in new tab button
  const OpenInNewTabButton = ({ imagePath }) => (
    <a 
      href={`${imagePath}.webp`} 
      target="_blank" 
      rel="noopener noreferrer"
      className="open-in-new-tab-btn"
    >
      Open in new tab
    </a>
  );

  // Labeled image component
  const LabeledImage = ({ src, webpSrc, alt, label }) => (
    <div style={{ position: "relative", width: "100%" }}>
      <ResponsiveImage
        src={src}
        webpSrc={webpSrc}
        alt={alt}
        style={{
          width: "100%",
          height: "auto",
          objectFit: "contain"
        }}
      />
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginTop: "10px"
      }}>
        <p style={{ 
          margin: 0,
          fontSize: "14px", 
          color: "#666",
          fontStyle: "italic"
        }}>
          {label}
        </p>
        <OpenInNewTabButton imagePath={webpSrc.replace('.webp', '')} />
      </div>
    </div>
  );

  return (
    <div style={{ overflowX: "hidden" }}>
      <Header />
      <ProjectNavigation currentProject="/senior-footwear-design-prototype" />
      
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "30px 20px" }}>
        <h1 style={{ textAlign: "center", marginBottom: "40px" }}>Lug Ballerina Footwear Prototype</h1>
        
        <p style={{
          maxWidth: "800px",
          margin: "20px auto 40px",
          fontSize: "16px",
          lineHeight: "1.6",
          color: "#333",
          padding: "0 20px",
          textAlign: "center"
        }}>
          A footwear prototype that I created fully from concept to prototyped realization in my senior footwear design elective. My goal was to examine and attempt to re-evaluate gender norms by encouraging more people to adopt tongueless, lower cut, loafer esque footwear like the ballerina by experimenting with design language and positioning.
        </p>

        {/* Ideation Sketches Section */}
        <section style={{ marginBottom: "60px" }}>
          <h2 style={{ marginBottom: "30px" }}>Ideation Sketches</h2>
          <div style={{ 
            position: "relative",
            width: "100%",
            maxWidth: "800px",
            margin: "0 auto"
          }}>
            <LabeledImage
              src={`${ideationSketchesImage}.jpg`}
              webpSrc={`${ideationSketchesImage}.webp`}
              alt="Ideation Sketches"
              label="Original Ideation Sketches"
            />
          </div>
        </section>

        {/* Concept Section */}
        <section style={{ marginBottom: "60px" }}>
          <h2 style={{ marginBottom: "30px" }}>Concept</h2>
          <div style={{ 
            position: "relative",
            width: "100%",
            maxWidth: "750px",
            margin: "0 auto"
          }}>
            <LabeledImage
              src={`${aiRenderImage}.jpg`}
              webpSrc={`${aiRenderImage}.webp`}
              alt="AI Assisted Image-to-Image Rendering"
              label="AI Assisted Image-to-Image Rendering"
            />
          </div>
          <p style={{
            maxWidth: "800px",
            margin: "40px auto 0",
            fontSize: "16px",
            lineHeight: "1.6",
            color: "#333",
            padding: "0 20px",
            textAlign: "left"
          }}>
            This footwear concept was originally intended to use a white Vibram #884C Bristol cup sole with a white smooth grain leather upper and an elastic woven band around the sock liner. Components were switched to more commercially available items such as the Vibram #1375 Bifida cup sole in black and a full grain black leather upper and smooth grain black leather liner for weight. Other key design decisions were made such as including a grommeted elastic cord lock closure to add to visual appeal and be more easily adjustable.
          </p>
        </section>

        {/* Technical Sketch Section */}
        <section style={{ marginBottom: "60px" }}>
          <h2 style={{ marginBottom: "30px" }}>Technical Sketch</h2>
          <div style={{ 
            position: "relative",
            width: "100%",
            maxWidth: "800px",
            margin: "0 auto"
          }}>
            <LabeledImage
              src={`${technicalSketchImage}.png`}
              webpSrc={`${technicalSketchImage}.webp`}
              alt="Technical Sketch"
              label="Left Lateral Profile Technical Sketch"
            />
          </div>
        </section>

        {/* Process Section */}
        <section style={{ marginBottom: "60px" }}>
          <h2 style={{ marginBottom: "30px" }}>Process</h2>
          
          {/* Landscape process images (slash and spread, one side, felt prototype) */}
          <div style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "30px",
            marginBottom: "30px"
          }}>
            {processLandscapeImages.map((img, index) => (
              <div key={`process-landscape-${index}`} style={{ 
                width: "100%",
                maxWidth: "800px",
                margin: "0 auto"
              }}>
                <LabeledImage
                  src={`${img.path}.${img.ext}`}
                  webpSrc={`${img.path}.webp`}
                  alt={img.label}
                  label={img.label}
                />
              </div>
            ))}
          </div>

          {/* Portrait process images (both sides, on-last) */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 400px), 1fr))", 
            gap: "30px"
          }}>
            {processPortraitImages.map((img, index) => (
              <LabeledImage
                key={`process-portrait-${index}`}
                src={`${img.path}.${img.ext}`}
                webpSrc={`${img.path}.webp`}
                alt={img.label}
                label={img.label}
              />
            ))}
          </div>
        </section>

        {/* Final Section */}
        <section style={{ marginBottom: "60px" }}>
          <h2 style={{ marginBottom: "30px" }}>Final Design</h2>
          
          {/* Front view - own row */}
          <div style={{ 
            maxWidth: "500px",
            margin: "0 auto 40px"
          }}>
            <LabeledImage
              src={`${finalFrontImage.path}.jpg`}
              webpSrc={`${finalFrontImage.path}.webp`}
              alt={finalFrontImage.label}
              label={finalFrontImage.label}
            />
          </div>

          {/* Lateral images - own row */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(2, 1fr)", 
            gap: "30px",
            marginBottom: "40px"
          }}>
            {finalLateralImages.map((img, index) => (
              <LabeledImage
                key={`final-lateral-${index}`}
                src={`${img.path}.jpg`}
                webpSrc={`${img.path}.webp`}
                alt={img.label}
                label={img.label}
              />
            ))}
          </div>

          {/* Medial images - own row */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(2, 1fr)", 
            gap: "30px",
            marginBottom: "40px"
          }}>
            {finalMedialImages.map((img, index) => (
              <LabeledImage
                key={`final-medial-${index}`}
                src={`${img.path}.jpg`}
                webpSrc={`${img.path}.webp`}
                alt={img.label}
                label={img.label}
              />
            ))}
          </div>

          {/* Lifestyle shots (on display, on foot) - 2 column centered */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(2, 1fr)", 
            gap: "30px",
            maxWidth: "800px",
            margin: "0 auto"
          }}>
            {finalLifestyleImages.map((img, index) => (
              <LabeledImage
                key={`final-lifestyle-${index}`}
                src={`${img.path}.jpg`}
                webpSrc={`${img.path}.webp`}
                alt={img.label}
                label={img.label}
              />
            ))}
          </div>
        </section>

        {/* Alternative Colorways Section */}
        <section style={{ marginBottom: "60px" }}>
          <h2 style={{ marginBottom: "30px" }}>Alternative Colorways</h2>
          <p style={{
            maxWidth: "800px",
            margin: "0 auto 30px",
            fontSize: "14px",
            lineHeight: "1.6",
            color: "#666",
            textAlign: "center",
            fontStyle: "italic"
          }}>
            These images were created image-to-image with the help of generative AI tools.
          </p>
          <div style={{ 
            display: "flex", 
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "30px"
          }}>
            {alternativeColorways.map((img, index) => (
              <div key={`alt-colorway-${index}`} style={{ width: "min(100%, 550px)" }}>
                <LabeledImage
                  src={`${img.path}.${img.ext}`}
                  webpSrc={`${img.path}.webp`}
                  alt={img.label}
                  label={img.label}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
};

export default FootwearDesign;
